<template>
  <section class="admin-content">
    <Navbar />
    <main class="admin-main">

      <!-- ── Page Header ─────────────────────────────────────── -->
      <div class="page-header">
        <div class="container">
          <div class="d-flex align-items-center text-white" style="padding: 32px 0;">
            <div class="header-icon-wrap mr-3">
              <i class="fe fe-users" style="font-size: 22px;"></i>
            </div>
            <div>
              <h4 class="mb-0 font-weight-bold">Personnel Management</h4>
              <small style="opacity: 0.8;">
                {{ Array.isArray(employees) ? employees.length : 0 }} employee(s) registered
              </small>
            </div>
            <div class="ml-auto">
              <button type="button" class="btn btn-light btn-sm font-weight-bold px-4"
                @click="showadder = !showadder">
                <i class="fe mr-1" :class="showadder ? 'fe-minus' : 'fe-plus'"></i>
                {{ showadder ? 'Cancel' : 'Add Personnel' }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="container mt-n3 pb-5">

        <!-- ── Add Employee Form ───────────────────────────────── -->
        <div v-show="showadder" class="card shadow-sm form-card mb-4">
          <div class="form-card-header">
            <div class="section-number">1</div>
            <h6 class="mb-0 font-weight-bold text-white">Add New Employee</h6>
          </div>
          <div class="card-body px-4 pt-4 pb-3">
            <div class="form-row">
              <div class="form-group col-md-6">
                <label class="small font-weight-bold text-secondary">
                  First Name <span class="text-danger">*</span>
                </label>
                <input type="text" class="form-control" v-model="register.firstname"
                  placeholder="First name" />
              </div>
              <div class="form-group col-md-6">
                <label class="small font-weight-bold text-secondary">
                  Last Name <span class="text-danger">*</span>
                </label>
                <input type="text" class="form-control" v-model="register.lastname"
                  placeholder="Last name" />
              </div>
              <div class="form-group col-md-6">
                <label class="small font-weight-bold text-secondary">
                  Email Address <span class="text-danger">*</span>
                </label>
                <input type="email" class="form-control" v-model="register.email"
                  placeholder="email@example.com" />
              </div>
              <div class="form-group col-md-6">
                <label class="small font-weight-bold text-secondary">
                  Phone Number <span class="text-danger">*</span>
                </label>
                <input v-phone type="text" class="form-control" v-model="register.phone_number"
                  placeholder="e.g. 08012345678" />
              </div>

              <!-- Job Title — hidden per existing logic -->
              <div class="form-group col-md-6" v-show="false">
                <label class="small font-weight-bold text-secondary">Job Title</label>
                <select class="form-control" v-model="register.job_title">
                  <option value="Desk">Desk Officer</option>
                  <option value="Doctor">Doctor</option>
                  <option value="Pharmacist">Pharmacist</option>
                  <option value="Nurse">Nurse</option>
                  <option value="Laboratorist">Laboratorist</option>
                  <option value="Accountant">Accountant</option>
                  <option value="Extension Worker">Community Health Extension Worker (CHEW)</option>
                </select>
              </div>
            </div>

            <div class="d-flex justify-content-end" style="gap: 10px;">
              <button type="button" class="btn btn-outline-secondary px-4"
                @click="showadder = false; clearIt()">
                Cancel
              </button>
              <button type="button" class="btn btn-submit px-5" @click="registerUser">
                <i class="fe fe-send mr-2"></i> Add Employee
              </button>
            </div>
          </div>
        </div>

        <!-- ── Employee Table ──────────────────────────────────── -->
        <div class="card shadow-sm table-card">
          <div class="d-flex align-items-center justify-content-between px-4 pt-4 pb-3">
            <div>
              <h6 class="mb-0 font-weight-bold text-dark">
                <i class="fe fe-list text-primary mr-2"></i> All Employees
              </h6>
              <small class="text-muted">
                {{ Array.isArray(employees) ? employees.length : 0 }} record(s)
              </small>
            </div>
          </div>

          <div class="table-responsive">
            <table class="table mb-0">
              <thead class="table-head">
                <tr>
                  <th class="table-th px-4">Name</th>
                  <th class="table-th">Email</th>
                  <th class="table-th">Phone Number</th>
                  <th class="table-th">Action</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="employee in employees" :key="employee.id" class="table-row">
                  <td class="px-4 py-3">
                    <div class="d-flex align-items-center" style="gap: 10px;">
                      <div class="employee-avatar">
                        {{ (employee.firstname || '?')[0].toUpperCase() }}
                      </div>
                      <div>
                        <p class="mb-0 font-weight-bold" style="font-size: .88rem;">
                          {{ employee.firstname }} {{ employee.lastname }}
                          <span v-if="employee.blocked_at" class="badge badge-secondary ml-1" style="font-size: .65rem;">Blocked</span>
                        </p>
                      </div>
                    </div>
                  </td>
                  <td class="py-3" style="font-size: .88rem;">{{ employee.email }}</td>
                  <td class="py-3" style="font-size: .88rem;">{{ employee.phone_number }}</td>
                  <td class="py-3">
                    <div class="d-flex" style="gap: 6px;">
                      <router-link :to="{ path: '/password-employee/' + employee.id }">
                        <button type="button" class="btn btn-sm btn-outline-primary">
                          <i class="fe fe-lock mr-1"></i> Password
                        </button>
                      </router-link>
                      <button
                        v-if="!employee.blocked_at"
                        type="button" class="btn btn-sm btn-outline-danger"
                        title="Remove personnel"
                        @click="removeEmployee(employee)"
                      >
                        <i class="fe fe-trash-2"></i>
                      </button>
                      <button
                        v-else
                        type="button" class="btn btn-sm btn-outline-success"
                        title="Restore personnel"
                        @click="restoreEmployee(employee)"
                      >
                        <i class="fe fe-check-circle"></i>
                      </button>
                      <button
                        type="button" class="btn btn-sm btn-outline-secondary"
                        title="Transfer to another facility"
                        @click="openTransferModal(employee)"
                      >
                        <i class="fe fe-shuffle"></i>
                      </button>
                    </div>
                  </td>
                </tr>
                <tr v-if="!employees || employees.length === 0">
                  <td colspan="4" class="text-center text-muted py-5">
                    <i class="fe fe-users" style="font-size: 2rem; display: block; margin-bottom: 8px;"></i>
                    No employees found
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

      </div>

      <!-- ── Transfer Personnel Modal ─────────────────────────── -->
      <div class="transfer-modal-overlay" v-if="transferModal.open" @click.self="closeTransferModal">
        <div class="card shadow transfer-modal">
          <div class="d-flex align-items-center justify-content-between px-4 py-3 border-bottom">
            <h6 class="mb-0 font-weight-bold">
              Transfer {{ transferModal.employee && transferModal.employee.firstname }}
              {{ transferModal.employee && transferModal.employee.lastname }}
            </h6>
            <button type="button" class="btn btn-sm btn-outline-secondary" @click="closeTransferModal">
              <i class="fe fe-x"></i>
            </button>
          </div>
          <div class="px-4 py-3">
            <div v-if="transferModal.loadingProviders" class="text-center text-muted py-3">
              <i class="fe fe-loader"></i> Loading facilities…
            </div>
            <template v-else>
              <label class="small font-weight-bold text-secondary" for="destinationFacilitySelect">Destination Facility</label>
              <select id="destinationFacilitySelect" class="form-control mb-3" v-model="transferModal.selectedProviderId">
                <option value="">Select a facility…</option>
                <option
                  v-for="provider in transferModal.providers" :key="provider.id"
                  :value="provider.id"
                  :disabled="provider.id == $route.params.id"
                >
                  {{ provider.agency_name }}{{ provider.id == $route.params.id ? ' (current facility)' : '' }}
                </option>
              </select>
              <button
                type="button" class="btn btn-submit btn-block"
                :disabled="!transferModal.selectedProviderId || transferModal.submitting"
                @click="confirmTransfer"
              >
                <i class="fe fe-shuffle mr-1"></i>
                {{ transferModal.submitting ? 'Transferring…' : 'Confirm Transfer' }}
              </button>
            </template>
          </div>
        </div>
      </div>

      <div class="vld-parent">
        <loading :active.sync="isLoading" loader="dots" :can-cancel="true" :is-full-page="fullPage"></loading>
      </div>
    </main>
  </section>
</template>

<script>
import Navbar from "@/views/Navbar.vue";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";
let agency = JSON.parse(localStorage.getItem("agency"));

export default {
  components: {
    Navbar,
    Loading,
  },
  data() {
    return {
      agency: agency,
      isLoading: false,
      fullPage: true,
      states: "",
      showadder: false,
      employees: "",
      state: "",
      lga_states: "",
      wards: "",
      register: {
        firstname: "",
        lastname: "",
        email: "",
        phone_number: "",
        type: "provider_employee",
        state: "",
        username: "",
        institutional_id: "",
        job_title: "Desk",
        lga: "",
        ward: "",
        address: "",
      },
      transferModal: {
        open: false,
        loadingProviders: false,
        submitting: false,
        employee: null,
        providers: [],
        selectedProviderId: "",
      },
    };
  },
  beforeMount() {
    this.user = JSON.parse(localStorage.getItem("user"));
    this.axios
      .get(`/api/v1/auth/getEmployee/${this.$route.params.id}`)
      .then((response) => {
        this.employees = response.data.data;
        console.log(response);
      })
      .catch((error) => {
        console.error(error);
      });
  },
  methods: {
    getEmployees() {
      this.user = JSON.parse(localStorage.getItem("user"));
      this.axios
        .get(`/api/v1/auth/getEmployee/${this.$route.params.id}`)
        .then((response) => {
          this.employees = response.data.data;
          console.log(response);
        })
        .catch((error) => {
          console.error(error);
        });
    },

    registerUser() {
      this.user = JSON.parse(localStorage.getItem("user"));
      this.isLoading = true;
      this.axios
        .post("/api/v1/auth/registerProvider", {
          firstname: this.register.firstname,
          lastname: this.register.lastname,
          email: this.register.email,
          phone_number: this.register.phone_number,
          type: this.register.type,
          username: this.register.email,
          password: "euhler",
          state: agency.state,
          institutional_id: this.$route.params.id,
          job_title: this.register.job_title,
          role: 0,
          localgovt: this.register.localgovt,
          ward: this.register.ward,
        })
        .then((response) => {
          console.log(response);
          this.isLoading = false;
          this.$breadstick.notify(
            "Employee added successfully! Default password is 'euhler'",
            { position: "top-right" }
          );
          this.getEmployees();
          this.clearIt();
          this.showadder = false;
        })
        .catch((error) => {
          console.log(error.response);
          this.isLoading = false;
          this.$breadstick.notify("Oops! something went wrong", {
            position: "top-right",
          });
        });
    },
    removeEmployee(employee) {
      if (!confirm(`Remove ${employee.firstname} ${employee.lastname} from active personnel? Their access will be disabled but their records are kept.`)) {
        return;
      }
      this.setEmployeeBlockedState(employee, "2021-01-01", "Personnel removed successfully!");
    },
    restoreEmployee(employee) {
      if (!confirm(`Restore ${employee.firstname} ${employee.lastname} to active personnel?`)) {
        return;
      }
      this.setEmployeeBlockedState(employee, null, "Personnel restored successfully!");
    },
    setEmployeeBlockedState(employee, blocked_at, successMessage) {
      this.isLoading = true;
      this.axios
        .patch(`/api/v1/auth/block-user/${employee.id}`, { blocked_at })
        .then(() => {
          this.isLoading = false;
          this.$breadstick.notify(successMessage, { position: "top-right" });
          this.getEmployees();
        })
        .catch((error) => {
          console.error(error);
          this.isLoading = false;
          this.$breadstick.notify("Oops! something went wrong", {
            position: "top-right",
          });
        });
    },
    openTransferModal(employee) {
      this.transferModal = {
        open: true,
        loadingProviders: true,
        submitting: false,
        employee,
        providers: [],
        selectedProviderId: "",
      };
      this.axios
        .get(`/api/v1/auth/providerAgency/${agency.id}`)
        .then((response) => {
          this.transferModal.providers = (response.data.data || []).filter(
            (p) => p.status !== false && p.status !== 0
          );
          this.transferModal.loadingProviders = false;
        })
        .catch((error) => {
          console.error(error);
          this.transferModal.loadingProviders = false;
          this.$breadstick.notify("Failed to load facility list", { position: "top-right" });
        });
    },
    closeTransferModal() {
      this.transferModal.open = false;
    },
    confirmTransfer() {
      const employee = this.transferModal.employee;
      const newProviderId = this.transferModal.selectedProviderId;
      if (!newProviderId || !confirm(`Transfer ${employee.firstname} ${employee.lastname} to the selected facility?`)) {
        return;
      }
      this.transferModal.submitting = true;
      this.axios
        .put(`/api/v1/auth/update-staff/${employee.id}`, {
          institutional_id: newProviderId,
          firstname: employee.firstname,
          lastname: employee.lastname,
          phone_number: employee.phone_number,
          email: employee.email,
        })
        .then(() => {
          this.transferModal.submitting = false;
          this.transferModal.open = false;
          this.$breadstick.notify("Personnel transferred successfully!", { position: "top-right" });
          this.getEmployees();
        })
        .catch((error) => {
          console.error(error);
          this.transferModal.submitting = false;
          this.$breadstick.notify("Failed to transfer personnel", { position: "top-right" });
        });
    },
    clearIt() {
      this.register.firstname = "";
      this.register.lastname = "";
      this.register.email = "";
      this.register.phone_number = "";
      this.register.job_title = "Desk";
    },
  },
  created() {
    this.getEmployees();
  },
};
</script>

<style scoped>
  /* ── Page Header ─────────────────────────────────────── */
  .page-header {
    background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);
    padding-bottom: 60px;
  }

  .header-icon-wrap {
    width: 48px;
    height: 48px;
    background: rgba(255, 255, 255, 0.18);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  /* ── Form Card ───────────────────────────────────────── */
  .form-card {
    border-radius: 14px;
    border: none;
    overflow: hidden;
  }

  .form-card-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px 24px;
    background: linear-gradient(135deg, #1e3a5f, #2563eb);
  }

  .section-number {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.25);
    color: #fff;
    font-size: 13px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  /* ── Submit Button ───────────────────────────────────── */
  .btn-submit {
    background: linear-gradient(135deg, #2563eb, #7c3aed);
    color: #fff;
    border: none;
    border-radius: 8px;
    font-weight: 600;
  }

  .btn-submit:hover {
    opacity: 0.92;
    color: #fff;
  }

  /* ── Table Card ──────────────────────────────────────── */
  .table-card {
    border-radius: 14px;
    border: none;
    overflow: hidden;
  }

  .table-head {
    background: #f8fafc;
  }

  .table-th {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #64748b;
    font-weight: 600;
    border-bottom: 2px solid #e2e8f0;
    padding: 12px 16px;
    white-space: nowrap;
  }

  .table-row {
    border-color: #f1f5f9;
    transition: background .15s;
  }

  .table-row:hover {
    background: #f8fafc;
  }

  /* ── Avatar chip ─────────────────────────────────────── */
  .employee-avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: linear-gradient(135deg, #1e3a5f, #2563eb);
    color: #fff;
    font-size: 0.85rem;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  /* ── Form controls ───────────────────────────────────── */
  .form-control:focus {
    border-color: #2563eb;
    box-shadow: 0 0 0 0.15rem rgba(37, 99, 235, 0.2);
  }

  /* ── Transfer Modal ───────────────────────────────────── */
  .transfer-modal-overlay {
    position: fixed; inset: 0; z-index: 1050;
    background: rgba(0,0,0,0.5);
    display: flex; align-items: center; justify-content: center; padding: 24px;
  }
  .transfer-modal { width: 100%; max-width: 420px; }
</style>
